/* ==================================================================
 * CartDrawer — the slide-out cart panel.
 *
 * Every cart mutation (qty +/-, direct input, addon ATC, product-form
 * ATC, selling-plan switch) follows one pipeline:
 *
 *   1. Shopify API call  (cart/add.js  or  cart/change.js)
 *   2. publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData })
 *   3. CartDrawer.onCartUpdate()  →  refreshFromSection()
 *   4. After DOM replacement:  refreshShippingBar()  [single paint]
 *
 * This guarantees a single section fetch and a single shipping-bar
 * repaint per mutation — no flicker, no stale DOM references.
 * ================================================================== */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.preventFormSubmission();
    this.refreshShippingBar();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.onCartUpdate(event);
      });
    }
  }

  disconnectedCallback() {
    if (this._cartUpdateUnsub) this._cartUpdateUnsub();
  }

  /* ============================================================
   * Cart-update handler (pub/sub)
   *
   * Single entry point for ALL cart mutations. Determines whether
   * to open the drawer, then delegates to refreshFromSection().
   * ============================================================ */
  onCartUpdate(event = {}) {
    const source = event.source || '';
    const shouldOpen = source === 'cart-drawer-addon' || source === 'product-form';

    this.classList.add('is-updating');

    this.refreshFromSection({
      open: shouldOpen,
      focusLine: event.focusLine || null
    });
  }

  /* ============================================================
   * Section refresh — single fetch, single DOM write
   *
   * Fetches ?section_id=cart-drawer, replaces .drawer__inner,
   * then runs every post-render hook exactly once.
   * ============================================================ */
  refreshFromSection({ open = false, focusLine = null } = {}) {
    const body = this.querySelector('.cart-drawer-v2__body');
    const scrollTop = body ? body.scrollTop : 0;
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    return fetch(`${cartUrl}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sourceInner = doc.querySelector('.drawer__inner');
        const destInner = this.querySelector('.drawer__inner');
        if (sourceInner && destInner) destInner.innerHTML = sourceInner.innerHTML;

        const sourceDrawer = doc.querySelector('cart-drawer');
        if (sourceDrawer) {
          this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
        }

        this.afterDOMReplacement(scrollTop);

        if (open) this.open();

        if (focusLine) {
          const input = this.querySelector(`#Drawer-quantity-${focusLine}, [data-index="${focusLine}"]`);
          if (input) input.focus();
        }
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.classList.remove('is-updating');
      });
  }

  /* ============================================================
   * Post-render hooks — called once after every DOM replacement
   * ============================================================ */
  afterDOMReplacement(scrollTop = 0) {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    const nextBody = this.querySelector('.cart-drawer-v2__body');
    if (nextBody) nextBody.scrollTop = scrollTop;

    this.preventFormSubmission();
    this.refreshShippingBar();
    this.syncHeaderCartCount();
  }

  /* ============================================================
   * Form-submit prevention
   *
   * The quantity inputs sit inside <form id="CartDrawer-Form">.
   * The checkout button (name="checkout") uses the form attribute
   * to associate with the form, making it the default submit
   * button. Pressing Enter in any input triggers implicit form
   * submission through that button, navigating to /cart.
   *
   * Instead of intercepting the submit event (which can't
   * reliably distinguish an Enter keypress from a checkout click
   * because e.submitter points to the checkout button in both
   * cases), we intercept the keydown on quantity inputs and
   * blur the field — which triggers the change event that the
   * CartDrawerItems.onChange handler picks up.
   *
   * Called on init and after every DOM replacement.
   * ============================================================ */
  preventFormSubmission() {
    const form = this.querySelector('#CartDrawer-Form');
    if (!form) return;
    form.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('input[type="number"]')) {
        e.preventDefault();
        e.target.blur();
      }
    });
  }

  /* ============================================================
   * Shipping bar — single paint, no flicker
   *
   * Always reads from data attributes on [data-shipping-bar]
   * which are fresh after section replacement. Uses the server-
   * computed presentment threshold when available to avoid
   * client-side float rounding errors.
   * ============================================================ */
  refreshShippingBar() {
    const bar = this.querySelector('[data-shipping-bar]');
    if (!bar) return;

    const thresholdShopCents = parseInt(this.dataset.shippingThresholdShopCents || '0', 10);
    if (!thresholdShopCents) return;

    const progressText = this.dataset.shippingProgressText || 'Add [amount] to unlock free shipping';
    const unlockedText = this.dataset.shippingUnlockedText || 'Free shipping unlocked!';

    const serverThreshold = parseInt(bar.dataset.shippingThresholdPresentmentCents || '0', 10);
    let thresholdPresentmentCents;
    if (serverThreshold > 0) {
      thresholdPresentmentCents = serverThreshold;
    } else {
      let rate = 1;
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
        rate = parseFloat(window.Shopify.currency.rate) || 1;
      }
      thresholdPresentmentCents = Math.round(thresholdShopCents * rate);
    }

    const totalCents = parseInt(bar.dataset.cartTotalCents || '0', 10);
    const currency = bar.dataset.cartCurrencyCode
      || (window.Shopify && Shopify.currency && Shopify.currency.active)
      || this.dataset.cartCurrency
      || this.dataset.shopCurrency
      || '';

    const remainingCents = Math.max(thresholdPresentmentCents - totalCents, 0);

    // Skip repaint when the bar already shows "unlocked" and the cart
    // is still above the threshold — avoids unnecessary DOM writes.
    if (remainingCents === 0 && bar.classList.contains('is-unlocked')) {
      bar.classList.remove('is-computing');
      return;
    }

    const pct = thresholdPresentmentCents > 0
      ? Math.min((totalCents / thresholdPresentmentCents) * 100, 100)
      : 0;

    this.paintShippingBar(bar, { remainingCents, pct, currency, progressText, unlockedText });
    bar.classList.remove('is-computing');
  }

  paintShippingBar(bar, { remainingCents, pct, currency, progressText, unlockedText }) {
    const message = bar.querySelector('[data-shipping-message]');
    const fill = bar.querySelector('.cart-drawer-v2__shipping-fill');
    const truck = bar.querySelector('.cart-drawer-v2__shipping-truck');
    const track = bar.querySelector('.cart-drawer-v2__shipping-track');

    if (fill) fill.style.width = pct + '%';
    const truckPct = Math.max(pct - 2, 0);
    if (truck) truck.style.left = truckPct + '%';
    if (track) track.setAttribute('aria-valuenow', String(Math.round(pct)));
    bar.classList.toggle('is-unlocked', remainingCents === 0);

    if (!message) return;
    if (remainingCents === 0) {
      message.innerHTML = `
        <span class="cart-drawer-v2__shipping-check" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 10.5l4 4 8-9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span>${this.escapeHTML(unlockedText)}</span>`;
    } else {
      const formatted = this.formatMoney(remainingCents, currency);
      const text = progressText.replace('[amount]', formatted);
      message.innerHTML = `<span>${this.escapeHTML(text)}</span>`;
    }
  }

  /* ============================================================
   * Header cart-count badge sync
   * ============================================================ */
  syncHeaderCartCount() {
    const badge = this.querySelector('[data-cart-drawer-count]');
    if (!badge) return;

    const count = badge.textContent.trim();
    const el = document.getElementById('cart-count');
    const bubble = document.getElementById('cart-count-bubble');

    if (el) {
      el.textContent = count;
    } else if (bubble && parseInt(count, 10) > 0) {
      bubble.innerHTML = `<span id="cart-count" aria-hidden="true">${count}</span>`;
    }
  }

  /* ============================================================
   * Drawer open / close
   * ============================================================ */
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const summaryEl = this.querySelector('[id^="Details-"] summary');
    if (summaryEl && !summaryEl.hasAttribute('role')) this.setSummaryAccessibility(summaryEl);

    setTimeout(() => { this.classList.add('animate', 'active'); });

    this.addEventListener('transitionend', () => {
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty') || this.querySelector('.drawer__inner')
        : document.getElementById('CartDrawer');
      const focusEl = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (container && focusEl && typeof trapFocus === 'function') {
        trapFocus(container, focusEl);
      }
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  /* ============================================================
   * Header cart-link hijack (open drawer instead of /cart)
   * ============================================================ */
  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(
      '#cart-icon-bubble, a[href="/cart"], a[href$="/cart"], .js-cart-drawer-open'
    );
    if (!cartLinks.length) return;

    cartLinks.forEach((link) => {
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
      link.addEventListener('click', (e) => { e.preventDefault(); this.open(link); });
      link.addEventListener('keydown', (e) => {
        if (e.code.toUpperCase() === 'SPACE') { e.preventDefault(); this.open(link); }
      });
    });
  }

  /* ============================================================
   * Accessibility helper for <details>/<summary>
   * ============================================================ */
  setSummaryAccessibility(summary) {
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', 'false');
    if (summary.nextElementSibling && summary.nextElementSibling.id) {
      summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    }
    summary.addEventListener('click', (e) => {
      e.currentTarget.setAttribute('aria-expanded',
        !e.currentTarget.closest('details').hasAttribute('open'));
    });
    if (typeof onKeyUpEscape === 'function') {
      summary.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  /* ============================================================
   * renderContents — used by product-form.js for quick-add-modal.
   * For normal ATC the pub/sub path handles everything, but the
   * quick-add modal needs the "modalClosed" sequencing so we keep
   * this method as a thin wrapper around refreshFromSection().
   * ============================================================ */
  renderContents(_parsedState) {
    this.classList.add('is-updating');
    this.refreshFromSection({ open: true });
  }

  /* ============================================================
   * Utilities
   * ============================================================ */
  getSectionsToRender() {
    const sections = [{ id: 'cart-drawer', selector: '#CartDrawer' }];
    if (document.getElementById('cart-icon-bubble')) {
      sections.push({ id: 'cart-icon-bubble' });
    }
    return sections;
  }

  formatMoney(cents, currencyCode) {
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol'
      }).format(amount);
    } catch (_) {
      return currencyCode + ' ' + amount.toFixed(2);
    }
  }

  escapeHTML(str) {
    return String(str).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);


/* ==================================================================
 * CartDrawerItems — extends CartItems for the drawer context.
 *
 * Overrides the parent's update methods so they never inject
 * cart-page markup into the drawer. Instead, every mutation
 * publishes PUB_SUB_EVENTS.cartUpdate and lets CartDrawer handle
 * the section refresh.
 * ================================================================== */

class CartDrawerItems extends CartItems {
  connectedCallback() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-drawer-items') return;
      this.onCartUpdate(event);
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  onCartUpdate() {
    // No-op: prevents CartItems.onCartUpdate() from fetching
    // the cart-page section into the drawer.
  }

  getSectionsToRender() {
    const sections = [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' }
    ];
    if (document.getElementById('cart-icon-bubble')) {
      sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    return sections;
  }

  onChange(event) {
    const target = event.target;
    if (target && target.matches && target.matches('[data-cart-selling-plan]')) {
      const line = parseInt(target.dataset.line, 10);
      this.updateSellingPlan(line, target.value || null);
      return;
    }
    this.updateQuantity(
      target.dataset.index,
      target.value,
      document.activeElement.getAttribute('name')
    );
  }

  /* ============================================================
   * updateQuantity — POST to cart/change.js, publish, done.
   *
   * No enableLoading/disableLoading (DOM gets replaced anyway).
   * The is-updating class on <cart-drawer> provides visual feedback.
   * ============================================================ */
  updateQuantity(line, quantity, _name) {
    const cartDrawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
    if (cartDrawer) cartDrawer.classList.add('is-updating');

    const body = JSON.stringify({ line, quantity });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => {
        const parsed = JSON.parse(state);
        if (parsed.errors) {
          const qtyEl = document.getElementById(`Drawer-quantity-${line}`);
          if (qtyEl) qtyEl.value = qtyEl.getAttribute('value');
          if (cartDrawer) cartDrawer.classList.remove('is-updating');
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-items',
          cartData: parsed,
          focusLine: line
        });
      })
      .catch((e) => {
        console.error(e);
        if (cartDrawer) cartDrawer.classList.remove('is-updating');
      });
  }

  /* ============================================================
   * updateSellingPlan — same pattern as updateQuantity.
   * ============================================================ */
  updateSellingPlan(line, sellingPlanId) {
    const cartDrawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
    if (cartDrawer) cartDrawer.classList.add('is-updating');

    const body = JSON.stringify({ line, selling_plan: sellingPlanId || '' });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((r) => r.text())
      .then((raw) => {
        const parsed = JSON.parse(raw);
        if (parsed.errors) {
          if (cartDrawer) cartDrawer.classList.remove('is-updating');
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-items',
          cartData: parsed,
          focusLine: line
        });
      })
      .catch((e) => {
        console.error(e);
        if (cartDrawer) cartDrawer.classList.remove('is-updating');
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


/* ==================================================================
 * CartDrawerAddon — the "+ Add" button on each addon slide.
 * ================================================================== */

class CartDrawerAddon extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.button.addEventListener('click', this.onAdd.bind(this));
  }

  onAdd(event) {
    event.preventDefault();
    if (this.button.disabled || this.button.classList.contains('is-loading')) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.button.classList.add('is-loading');
    this.button.setAttribute('aria-disabled', 'true');

    const cartDrawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
    if (cartDrawer) cartDrawer.classList.add('is-updating');

    const sellingPlanId = this.dataset.sellingPlanId;
    const item = { id: Number(variantId), quantity: 1 };
    if (sellingPlanId) item.selling_plan = Number(sellingPlanId);

    const body = JSON.stringify({ items: [item] });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((r) => r.json())
      .then((response) => {
        if (response.status) {
          this.handleError(response.description || response.message);
          if (cartDrawer) cartDrawer.classList.remove('is-updating');
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-addon',
          productVariantId: variantId,
          cartData: response
        });
      })
      .catch((err) => {
        console.error(err);
        this.handleError();
        if (cartDrawer) cartDrawer.classList.remove('is-updating');
      })
      .finally(() => {
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-disabled');
      });
  }

  handleError(message) {
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors) {
      errors.textContent = message || (window.cartStrings && window.cartStrings.error) || '';
    } else if (message) {
      console.error('Addon add error:', message);
    }
  }
}

customElements.define('cart-drawer-addon', CartDrawerAddon);


/* ==================================================================
 * CartDrawerAddons — scroll-snap carousel for the addons rail.
 * ================================================================== */

class CartDrawerAddons extends HTMLElement {
  constructor() {
    super();
    this.viewport = this.querySelector('[data-addons-viewport]');
    this.track = this.querySelector('[data-addons-track]');
    this.prevBtn = this.querySelector('[data-addons-prev]');
    this.nextBtn = this.querySelector('[data-addons-next]');
    this.dotsContainer = this.querySelector('[data-addons-dots]');
    if (!this.track || !this.viewport) return;

    this.slides = Array.from(this.track.children);
    this.itemsPerView = this._computeItemsPerView();
    this.pageCount = Math.max(1, Math.ceil(this.slides.length / this.itemsPerView));
    this.currentPage = 0;

    this._renderDots();
    this._syncFromScroll();

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._go(this.currentPage - 1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._go(this.currentPage + 1));

    this._onScroll = this._debounce(() => this._syncFromScroll(), 80);
    this.viewport.addEventListener('scroll', this._onScroll, { passive: true });

    this._onResize = this._debounce(() => {
      const nextIPV = this._computeItemsPerView();
      if (nextIPV !== this.itemsPerView) {
        this.itemsPerView = nextIPV;
        this.pageCount = Math.max(1, Math.ceil(this.slides.length / this.itemsPerView));
        this._renderDots();
      }
      this._syncFromScroll();
    }, 120);
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    if (this._onScroll && this.viewport) this.viewport.removeEventListener('scroll', this._onScroll);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  _computeItemsPerView() {
    const cssValue = getComputedStyle(this).getPropertyValue('--cd-addons-items-per-view');
    const parsed = parseInt(cssValue, 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    return parseInt(this.dataset.itemsPerView || '1', 10) || 1;
  }

  _renderDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    if (this.pageCount <= 1) return;
    for (let i = 0; i < this.pageCount; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'cart-drawer-v2__addons-dot' + (i === this.currentPage ? ' is-active' : '');
      dot.setAttribute('aria-label', `Go to page ${i + 1}`);
      dot.addEventListener('click', () => this._go(i));
      this.dotsContainer.appendChild(dot);
    }
  }

  _go(page) {
    if (page < 0 || page >= this.pageCount) return;
    const pageWidth = this.viewport.clientWidth;
    this.viewport.scrollTo({ left: page * pageWidth, behavior: 'smooth' });
  }

  _syncFromScroll() {
    const pageWidth = this.viewport.clientWidth || 1;
    const rawPage = this.viewport.scrollLeft / pageWidth;
    const page = Math.min(Math.max(Math.round(rawPage), 0), this.pageCount - 1);
    this.currentPage = page;

    if (this.dotsContainer) {
      Array.from(this.dotsContainer.children).forEach((d, i) => {
        d.classList.toggle('is-active', i === page);
      });
    }

    const hasMultiple = this.pageCount > 1;
    if (this.prevBtn) {
      this.prevBtn.setAttribute('data-visible', hasMultiple ? 'true' : 'false');
      this.prevBtn.disabled = page === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.setAttribute('data-visible', hasMultiple ? 'true' : 'false');
      this.nextBtn.disabled = page >= this.pageCount - 1;
    }
  }

  _debounce(fn, ms) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }
}

customElements.define('cart-drawer-addons', CartDrawerAddons);
